import { useCallback, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

import { BalanceResponse } from "../api/subscription.api";
import { isPaymentHandled } from "../helper_lib/preview-upgrade-return";
import { useBalanceStore } from "../store/balance.store";

// БЕЗОПАСНОСТЬ: WebSocket использует JWT аутентификацию через cookies + WSS (HTTPS)
// Для REST API используется VITE_API_BASE_URL с /api
// Для WebSocket нужен базовый URL БЕЗ /api (socket.io сам добавляет /socket.io)
const getWebSocketURL = () => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8050";
  return apiBaseUrl.replace(/\/api$/, "");
};

const BACKEND_URL = getWebSocketURL();

/**
 * Хук для подключения к WebSocket для реал-тайм обновлений баланса
 */
export const useBalanceWebSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const setBalance = useBalanceStore(state => state.setBalance);
  const setLoading = useBalanceStore(state => state.setLoading);
  const setError = useBalanceStore(state => state.setError);

  const connect = useCallback(() => {
    // Если сокет уже подключен, не создаем новый
    if (socketRef.current?.connected) {
      return;
    }

    // Очищаем предыдущий сокет если он есть
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    // На проде используем ТОЛЬКО WebSocket (статус 101)
    // Polling создает много HTTP запросов со статусом 200 и "Finished"
    // REST запросы работают отдельно через обычный HTTP
    const socket = io(`${BACKEND_URL}/balance`, {
      withCredentials: true,
      transports: ["websocket"], // Только WebSocket, без polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    socketRef.current = socket;

    // Обработчик успешного подключения
    socket.on("connect", () => {
      setError(null);

      // Подписываемся на обновления баланса
      socket.emit("balance:subscribe");
    });

    // Обработчик ping от сервера (heartbeat)
    socket.on("ping", () => {
      // Автоматически отвечаем pong
      socket.emit("pong");
    });

    // Обработчик получения обновления баланса
    socket.on("balance:updated", (balance: BalanceResponse) => {
      setBalance(balance);
      setLoading(false);
    });

    // Обработчик успешной оплаты - редирект на страницу успеха
    socket.on(
      "payment:success",
      (paymentData: { paymentId: string; type: string; amount: number; recurring?: boolean; seamless?: boolean }) => {
        if (paymentData.recurring) {
          return;
        }

        // Списание с сохранённой карты по кнопке: человек остался на своей
        // странице и уже видит результат в модалке — уводить вкладку некуда
        if (paymentData.seamless) {
          return;
        }

        const currentPath = window.location.pathname;

        // Платёж уже обработан: пользователя увели к его записи после превью.
        // Опоздавшее событие вебхука иначе утащило бы его обратно на страницу
        // успеха — ровно тот лишний переход, который бесшовный возврат убирает
        if (isPaymentHandled(paymentData.paymentId)) {
          return;
        }

        if (
          currentPath !== "/payment/subscription/success" &&
          currentPath !== "/payment/minutes/success" &&
          currentPath !== "/payment/fail"
        ) {
          const successPath =
            paymentData.type === "subscription" ? "/payment/subscription/success" : "/payment/minutes/success";
          window.location.href = `${successPath}?paymentId=${paymentData.paymentId}`;
        }
      }
    );

    // Обработчик ошибки подключения
    socket.on("connect_error", error => {
      console.error(`❌ [BALANCE WS] Connection error: ${error.message}`, error);
      setError("Ошибка подключения к серверу");

      // Автоматическое переподключение через 3 секунды
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        socket.connect();
      }, 3000);
    });

    // Обработчик отключения
    socket.on("disconnect", reason => {
      console.warn(`⚠️ [BALANCE WS] Disconnected. Reason: ${reason}`);

      // Переподключаемся для любого типа разрыва кроме явного disconnect клиента
      if (reason !== "io client disconnect") {
        socket.connect();
      }
    });

    // Обработчик переподключения
    socket.on("reconnect", attemptNumber => {
      setError(null);
    });

    return socket;
  }, [setBalance, setLoading, setError]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Подключаемся при монтировании
    connect();

    // Отключаемся при размонтировании
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    socket: socketRef.current,
    connect,
    disconnect,
    isConnected: socketRef.current?.connected || false
  };
};
